import React from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface FloatingIslandCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  gradient?: string;
  index: number;
}

export const FloatingIslandCard = ({ 
  icon: Icon, 
  title, 
  description, 
  gradient = "from-primary/20 to-accent/20",
  index 
}: FloatingIslandCardProps) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [30, -30]);
  const rotateY = useTransform(x, [-100, 100], [-30, 30]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100, scale: 0.8 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.8, 
        delay: index * 0.15,
        type: "spring",
        stiffness: 80
      }}
      className="group relative h-full"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        perspective: "1000px"
      }}
    >
      {/* Floating shadows with depth */}
      <motion.div 
        className="absolute inset-0 bg-black/20 rounded-3xl blur-xl"
        initial={{ y: 20, opacity: 0.3 }}
        whileHover={{ y: 30, opacity: 0.6 }}
        transition={{ duration: 0.3 }}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d"
        }}
      />
      
      {/* Main floating island */}
      <motion.div
        className="relative bg-gradient-to-br from-purple-900/40 via-blue-800/30 to-cyan-700/40 backdrop-blur-2xl border border-cyan-400/50 rounded-3xl p-8 h-full overflow-hidden transition-all duration-500 group-hover:border-pink-400/70 group-hover:shadow-2xl group-hover:shadow-cyan-500/30"
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d"
        }}
        whileHover={{
          scale: 1.05,
          transition: { duration: 0.3 }
        }}
      >
        {/* Vibrant animated gradient backgrounds */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-purple-600/30 to-blue-500/20 opacity-60 group-hover:opacity-90 transition-all duration-500"
          animate={{
            background: [
              "linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(147, 51, 234, 0.3), rgba(59, 130, 246, 0.2))",
              "linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(16, 185, 129, 0.2), rgba(236, 72, 153, 0.3))",
              "linear-gradient(135deg, rgba(147, 51, 234, 0.2), rgba(236, 72, 153, 0.3), rgba(59, 130, 246, 0.2))"
            ]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Shimmering overlay */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
          animate={{
            x: ["-100%", "100%"]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            repeatDelay: 2
          }}
        />
        
        {/* Holographic rim lighting with rainbow effect */}
        <motion.div 
          className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-500" 
          style={{ 
            background: "conic-gradient(from 0deg, #ff0080, #ff8c00, #40e0d0, #ff0080)",
            maskImage: "linear-gradient(black, black), linear-gradient(black, black)",
            maskComposite: "subtract",
            padding: "2px",
            WebkitMask: "linear-gradient(black, black) content-box, linear-gradient(black, black)",
            WebkitMaskComposite: "xor"
          }}
          animate={{
            rotate: [0, 360]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        {/* Enhanced floating elements with colors */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-2 h-2 rounded-full ${
                i % 3 === 0 ? 'bg-pink-400/40' : 
                i % 3 === 1 ? 'bg-cyan-400/40' : 'bg-purple-400/40'
              }`}
              initial={{ 
                x: `${20 + Math.random() * 60}%`, 
                y: `${20 + Math.random() * 60}%`,
                scale: 0.5
              }}
              animate={{
                y: [
                  `${20 + Math.random() * 60}%`,
                  `${10 + Math.random() * 80}%`,
                  `${20 + Math.random() * 60}%`
                ],
                x: [
                  `${20 + Math.random() * 60}%`,
                  `${30 + Math.random() * 40}%`,
                  `${20 + Math.random() * 60}%`
                ],
                scale: [0.5, 1.2, 0.5],
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{
                duration: 6 + Math.random() * 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: Math.random() * 3
              }}
            />
          ))}
        </div>

        <div className="relative z-10">
          {/* Vibrant levitating icon */}
          <motion.div 
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-pink-500/30 via-purple-600/40 to-cyan-500/30 backdrop-blur-sm border border-cyan-400/60 rounded-2xl mb-6 group-hover:shadow-xl group-hover:shadow-pink-500/50 transition-all duration-300"
            whileHover={{ 
              y: -10,
              rotateZ: 10,
              scale: 1.1,
              transition: { duration: 0.4, type: "spring" }
            }}
            animate={{
              y: [0, -4, 0],
              boxShadow: [
                "0 0 20px rgba(236, 72, 153, 0.3)",
                "0 0 30px rgba(6, 182, 212, 0.4)", 
                "0 0 20px rgba(236, 72, 153, 0.3)"
              ]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Icon className="w-10 h-10 text-cyan-300 group-hover:text-pink-300 transition-colors duration-300" />
          </motion.div>
          
          {/* Title with neon effect */}
          <motion.h3 
            className="text-2xl font-bold text-cyan-100 mb-4 group-hover:text-pink-200 transition-colors duration-300"
            whileHover={{ 
              scale: 1.05,
              x: 5,
              textShadow: "0 0 20px rgba(236, 72, 153, 0.8)",
              transition: { duration: 0.2 }
            }}
            style={{
              textShadow: "0 0 10px rgba(6, 182, 212, 0.5)"
            }}
          >
            {title}
          </motion.h3>
          
          {/* Description with glow */}
          <motion.p 
            className="text-cyan-200/90 leading-relaxed group-hover:text-pink-100 transition-colors duration-300"
            initial={{ opacity: 0.9 }}
            whileHover={{ 
              opacity: 1, 
              x: 5,
              textShadow: "0 0 15px rgba(255, 255, 255, 0.3)"
            }}
            transition={{ duration: 0.3 }}
          >
            {description}
          </motion.p>
        </div>

        {/* Energy field visualization */}
        <div className="absolute bottom-0 right-0 w-32 h-32 opacity-10 group-hover:opacity-30 transition-opacity duration-500">
          <svg width="100%" height="100%" viewBox="0 0 128 128">
            {[...Array(5)].map((_, i) => (
              <motion.circle
                key={i}
                cx="64"
                cy="64"
                r={20 + i * 15}
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                className="text-primary"
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 0.3 }}
                transition={{ 
                  duration: 1.5, 
                  delay: i * 0.2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
            ))}
          </svg>
        </div>
      </motion.div>
    </motion.div>
  );
};